import {
  type PropsWithChildren,
  type RefObject,
  useEffect,
  useRef,
} from 'react'
import type React from 'react'
import { createPortal } from 'react-dom'

import { useOnClickOutside } from '../hooks/useOnClickOutside'

type PortalProps<TElement extends HTMLElement = HTMLDivElement> =
  PropsWithChildren<{
    id: string
    parentRef?: RefObject<TElement | null>
    onClickOutside?: (event: MouseEvent | TouchEvent) => void
  }>

export const Portal = <TElement extends HTMLElement = HTMLDivElement>({
  children,
  id,
  parentRef,
  // eslint-disable-next-line no-empty-function
  onClickOutside = () => {},
}: PortalProps<TElement>) => {
  const el = document.createElement('div')
  const wrapper: React.RefObject<HTMLElement> = useRef(el)
  useOnClickOutside([wrapper], onClickOutside)

  useEffect(() => {
    const parent = parentRef?.current ?? document.body
    const current = wrapper.current as HTMLElement
    current.setAttribute('id', id)
    parent.appendChild(current)

    return () => {
      parent.removeChild(current)
    }
  }, [id, parentRef])

  if (!wrapper.current) {
    return null
  }

  return createPortal(children, wrapper.current)
}
